import { BaseService } from './BaseService.js';
import { frontendLogger } from '../../services/frontendLogger.js';

class PurchasesService extends BaseService {
  
  constructor() {
    super();
  }

  // Get user's purchase history
  async getUserPurchases(page = 1, limit = 10) {
    try {
      const response = await this.request(`/purchases?page=${page}&limit=${limit}`);
      return response;
    } catch (error) {
      frontendLogger.error('Error fetching user purchases', {
        error: error.message,
        stack: error.stack,
        page,
        limit,
        endpoint: `/purchases?page=${page}&limit=${limit}`
      }, 'PurchasesService');
      throw error;
    }
  }

  // Get specific purchase by ID
  async getPurchase(purchaseId) {
    try {
      const response = await this.request(`/purchases/${purchaseId}`);
      return response;
    } catch (error) {
      frontendLogger.error('Error fetching purchase', {
        error: error.message,
        stack: error.stack,
        purchaseId,
        endpoint: `/purchases/${purchaseId}`
      }, 'PurchasesService');
      throw error;
    }
  }

  // Get purchase statistics
  async getPurchaseStats() {
    try {
      const response = await this.request('/purchases/stats');
      return response;
    } catch (error) {
      frontendLogger.error('Error fetching purchase stats', {
        error: error.message,
        stack: error.stack,
        endpoint: '/purchases/stats'
      }, 'PurchasesService');
      throw error;
    }
  }

  // Get purchase tracking info
  async getTrackingInfo(purchaseId) {
    try {
      const purchase = await this.getPurchase(purchaseId);
      return purchase.data?.order_tracking || null;
    } catch (error) {
      frontendLogger.error('Error fetching tracking info', {
        error: error.message,
        stack: error.stack,
        purchaseId,
        trackingFlow: true
      }, 'PurchasesService');
      throw error;
    }
  }

  // Format purchase for display
  formatPurchase(purchase) {
    const purchaseDate = new Date(purchase.purchased_at);
    const category = purchase.shopItem?.category || purchase.category || 'Unknown';
    
    return {
      id: purchase.id,
      itemName: purchase.item_name,
      category: category,
      tier: purchase.tier,
      price: parseFloat(purchase.price_at_time),
      amount: parseFloat(purchase.amount),
      quantity: purchase.quantity,
      purchasedAt: purchaseDate,
      shopItem: purchase.shop_items,
      orderTracking: purchase.order_tracking,
      status: purchase.status,
      // FIXED: Include dedicated order_id column
      order_id: purchase.order_id,
      tracking_number: purchase.tracking_number,
      courier_service: purchase.courier_service,
      shipped_at: purchase.shipped_at,
      delivered_at: purchase.delivered_at,
      // Helper methods - using new status column
      isDigital: purchase.shop_items?.is_digital || false,
      isPending: purchase.status === 'pending',
      isProcessing: purchase.status === 'processing',
      isShipped: purchase.status === 'shipped',
      isDelivered: purchase.status === 'delivered',
      formattedPrice: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(parseFloat(purchase.price_at_time)),
      formattedDate: purchaseDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      formattedDateTime: purchaseDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  }

  // Format multiple purchases
  formatPurchases(purchases) {
    return purchases.map(purchase => this.formatPurchase(purchase));
  }
}

export default new PurchasesService();